import React from "react";
import { DocumentType } from "../types/DocumentType";
import { Pagination } from "../types/PagedResults";
import { useLazySearchMasterAccountDocumentsQuery } from "../services/clientdocuments";
import { useClientContext } from "./ClientContext";
import { useFilterContext } from "./FilterContext";

export type ClientDocumentsContextProps = {
    clear: () => void,
    data: DocumentType[],
    error: Error | string | null,
    hasMore: boolean,
    isFetching: boolean,
    isLoading: boolean,
    isNextPageLoading: boolean,
    isNewRecords: boolean,
    isRowLoaded: boolean,
    limit: number,
    loadMore: () => void,
    newRecordsSinceCache: number,
    page: number,
    pagination: Pagination,
    setPageLimit: (v: number) => void,
    setSearchTerm: (v: string) => void,
    sort: string,
    sortTable: (property: string) => void,
    totalCount: number,
    totalPages: number
};

export type ClientDocumentsProviderProps = {
    children: JSX.Element,
    filter?: any | null,
    isReady?: boolean
    masterAccountId: string | null
};

const ClientDocumentsContext = React.createContext<ClientDocumentsContextProps | null>(null);

export const useClientDocuments = () => React.useContext(ClientDocumentsContext);

export const ClientDocumentsFilteredProvider = ({ children }: Readonly<{ children: JSX.Element }>) => {
    const { id } = useClientContext();
    const { filter, isReady } = useFilterContext();
    return (
        <ClientDocumentsProvider masterAccountId={id} filter={filter} isReady={isReady}>
            {children}
        </ClientDocumentsProvider>
    );
};

export const ClientDocumentsProvider = ({
    children,
    filter = null,
    isReady = true,
    masterAccountId
}: Readonly<ClientDocumentsProviderProps>) => {
    // setup state and our query state (does not load by default (lazy))
    const [sort, setSort] = React.useState<string>("DateUploaded+desc");
    const [pageLimit, setPageLimit] = React.useState<number>(50);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [isClearing, setIsClearing] = React.useState<boolean>(false);
    const [isLoadingState, setIsLoadingState] = React.useState<boolean>(false);
    const [load, { data, isLoading, isFetching, ...loadResults }] = useLazySearchMasterAccountDocumentsQuery();

    // extract the results of the last load to our report search
    // note: we need to have a default set of data in-case we have an undefined response
    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isNextPageLoading = isFetching === true || isLoading === true || isLoadingState === true;

    const buildOptions = (cacheKey: number, page: number): any => {
        return filter
            ? {
                ...filter,
                masterAccountId,
                cacheKey,
                sort,
                page,
                limit: pageLimit,
                searchTerm
            }
            : {
                masterAccountId,
                cacheKey,
                sort,
                page,
                limit: pageLimit,
                searchTerm
            }
    };

    const clear = (): void => {
        if (!masterAccountId || isNextPageLoading || isClearing || !isReady) {
            return;
        }

        const newCacheKey = new Date().valueOf();
        const queryOptions = buildOptions(newCacheKey, 1);

        setIsLoadingState(_ => true);
        setIsClearing(_ => true);
        load(queryOptions)
            .unwrap()
            .then(() => {
                setCacheKey(_prev => newCacheKey);
            })
            .finally(() => {
                setIsLoadingState(_prev => false);
                setIsClearing(_ => false);
            });
    };

    const isRowLoaded = (index: number): boolean => {
        return !hasMore || index < results.length;
    }

    const loadMore = (): void => {
        if (isNextPageLoading || isClearing || !hasMore || !isReady) {
            return;
        }

        setIsLoadingState(_ => true);
        load(buildOptions(cacheKey, (page + 1))).unwrap().finally(() => {
            setIsLoadingState(_prev => false);
        });
    };

    const sortTable = (property: string): void => {
        // we want the sort to initially be descending ??
        let newDirection: string | null = null;
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
    };

    React.useEffect(clear, [searchTerm, sort, masterAccountId, filter, isReady]);

    return (
        <ClientDocumentsContext.Provider value={{
            ...loadResults,
            clear,
            data: isClearing ? [] : results,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isNewRecords: newRecordsSinceCache > 0,
            isRowLoaded,
            limit: pageLimit,
            loadMore: isNextPageLoading ? () => { } : loadMore,
            newRecordsSinceCache,
            page,
            pagination,
            setPageLimit: (v) => setPageLimit(() => v),
            setSearchTerm,
            searchTerm,
            sort,
            sortTable,
            totalCount,
            totalPages
        }}>
            {children}
        </ClientDocumentsContext.Provider>
    );
};